import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button, Card, ErrorBox, Field, Input, Select, Spinner } from "../../components/kd";
import { ROLLE, fmtDateTime } from "../../lib/format";

const EMPTY = { vorname: "", nachname: "", email: "", rolle: "MITARBEITER" };

export function UserSettings({ meId }: { meId: string }) {
  const [users, setUsers] = useState<any[] | null>(null);
  const [form, setForm] = useState<any>(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/benutzer").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/admin/benutzer/einladen", form); toast.success("Einladung versendet."); setForm(EMPTY); load(); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const update = async (id: string, patch: any) => {
    try { await api.put(`/admin/benutzer/${id}`, patch); toast.success("Gespeichert."); load(); } catch (ex) { toast.error(errMsg(ex)); }
  };

  const setTwoFactor = async (u: any) => {
    const aktiv = !!u.totp_deaktiviert;
    if (!aktiv && !window.confirm(`Zwei-Faktor-Code für ${u.email} wirklich ausschalten? Die Anmeldung erfolgt dann nur noch mit dem Passwort.`)) return;
    try { await api.put(`/admin/benutzer/${u.id}/zwei-faktor`, { aktiv }); toast.success(aktiv ? "Zwei-Faktor-Code wieder aktiviert." : "Zwei-Faktor-Code deaktiviert."); load(); } catch (ex) { toast.error(errMsg(ex)); }
  };

  return (
    <div className="space-y-5">
      <p className="rounded-lg border border-warning bg-warning-bg p-3 text-sm text-[#7A3E00]" data-testid="two-factor-warning">Ohne Zwei-Faktor-Code genügt für die Anmeldung allein das Passwort. Für den Echtbetrieb mit Patientendaten empfehlen wir, die Zwei-Faktor-Authentifizierung eingeschaltet zu lassen. Jede Änderung wird im Audit-Log festgehalten.</p>
      <Card className="!p-0 overflow-x-auto">
        {!users ? <Spinner /> : (
          <table className="w-full text-left text-sm" data-testid="users-table">
            <thead className="border-b border-border bg-bg text-text-muted"><tr>{["Name", "E-Mail", "Rolle", "Status", "Zwei-Faktor", "Letzte Anmeldung", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-3">{u.vorname} {u.nachname}</td>
                  <td className="px-3 py-3">{u.email}</td>
                  <td className="px-3 py-3">
                    {u.id === meId ? ROLLE[u.rolle] : <Select aria-label="Rolle" options={ROLLE} value={u.rolle} onChange={(e) => update(u.id, { rolle: e.target.value })} className="min-w-[150px]" data-testid={`user-role-${u.email}`} />}
                  </td>
                  <td className="px-3 py-3">{u.aktiv ? <span className="text-success">Aktiv</span> : <span className="text-text-muted">Deaktiviert</span>}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <span className={u.totp_deaktiviert ? "text-warning" : "text-success"} data-testid={`user-2fa-status-${u.email}`}>{u.totp_deaktiviert ? "Aus" : u.totp_eingerichtet ? "An" : "An (nicht eingerichtet)"}</span>
                      <Button variant="secondary" onClick={() => setTwoFactor(u)} data-testid={`user-2fa-toggle-${u.email}`}>{u.totp_deaktiviert ? "Einschalten" : "Ausschalten"}</Button>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">{u.letzter_login ? fmtDateTime(u.letzter_login) : "–"}</td>
                  <td className="px-3 py-3">
                    {u.id !== meId && <Button variant={u.aktiv ? "secondary" : "primary"} onClick={() => update(u.id, { aktiv: !u.aktiv })} data-testid={`user-toggle-${u.email}`}>{u.aktiv ? "Deaktivieren" : "Aktivieren"}</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card className="max-w-xl">
        <h2 className="mb-4 flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" aria-hidden />Benutzer einladen</h2>
        <form onSubmit={invite}>
          <ErrorBox testid="invite-error">{err}</ErrorBox>
          <Field label="Vorname" required htmlFor="iv"><Input id="iv" value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} data-testid="invite-vorname-input" /></Field>
          <Field label="Nachname" required htmlFor="in"><Input id="in" value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} data-testid="invite-nachname-input" /></Field>
          <Field label="E-Mail" required htmlFor="ie"><Input id="ie" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" /></Field>
          <Field label="Rolle" required htmlFor="ir"><Select id="ir" options={ROLLE} value={form.rolle} onChange={(e) => setForm({ ...form, rolle: e.target.value })} data-testid="invite-rolle-select" /></Field>
          <Button type="submit" loading={busy} data-testid="invite-submit-button">Einladung senden</Button>
        </form>
      </Card>
    </div>
  );
}
